import { useState } from 'react'
import {
  loadReviews,
  logReview,
  type ReviewKindAll,
  type ReviewMeeting,
} from '@/data/portal'
import { nextReviewDue, type ReviewDue } from '@core/rules/reviewCadence.js'
import { useAsync } from '@/data/useAsync'
import { Failed, Loading } from '@/components/bits'
import { longDate, shortDate, today } from '@/lib/format'
import type { PersonDetail } from '@/data/queries'
import { useOrg } from '@/org/OrgProvider'

const KIND_LABEL: Record<ReviewKindAll, string> = {
  supervision: 'Supervision',
  appraisal: 'Appraisal',
  probation_review: 'Probation review',
}

const STATE_WORDS: Record<ReviewDue['state'], string> = {
  upcoming: 'Next due',
  due_soon: 'Due soon',
  due: 'Due now',
  overdue: 'Overdue',
}

function Cadence({ due, label }: { due: ReviewDue | null; label: string }) {
  if (due === null) {
    return (
      <div className="box">
        <div className="eyebrow">{label}</div>
        <div className="due" style={{ color: 'var(--ivory-faint)' }}>
          Not required
        </div>
        <small>No interval is set for this department.</small>
      </div>
    )
  }
  return (
    <div className="box" data-state={due.state}>
      <div className="eyebrow">
        {label} &middot; every {due.intervalMonths} month{due.intervalMonths === 1 ? '' : 's'}
        {due.onProbationRhythm && ' (probation)'}
      </div>
      <div className="due">{shortDate(due.dueOn)}</div>
      <small>
        {STATE_WORDS[due.state]}.{' '}
        {due.neverHeld
          ? `Never held, so the clock has been running from the start date of ${shortDate(due.anchor)}.`
          : `Last held ${shortDate(due.anchor)}.`}
      </small>
    </div>
  )
}

function Meeting({ m }: { m: ReviewMeeting }) {
  const when = m.heldOn ?? m.scheduledFor
  return (
    <div className="tl">
      <div className="when">{when === null ? '—' : shortDate(when)}</div>
      <div className="what">
        <div className="headline">
          <strong>{KIND_LABEL[m.kind]}</strong>
          {m.status !== 'held' && <span className="sev info">{m.status}</span>}
          {m.concernsRaised && <span className="sev overdue">Concern raised</span>}
        </div>
        {m.outcome !== null && <p>Outcome: {m.outcome}</p>}
        {m.summary !== null && <p>{m.summary}</p>}
        {m.actionsAgreed !== null && (
          <p>
            <span style={{ color: 'var(--ivory-faint)' }}>Agreed: </span>
            {m.actionsAgreed}
          </p>
        )}
        <div className="meta">
          {m.conductedByName === null ? 'Conducted by a signed-in user' : `Conducted by ${m.conductedByName}`}
          {m.heldOn !== null && ` · held ${longDate(m.heldOn)}`}
        </div>
      </div>
    </div>
  )
}

export function Reviews({ person }: { person: PersonDetail }) {
  const { current } = useOrg()
  const result = useAsync(
    () => loadReviews(current.id, person.employment.id),
    [current.id, person.employment.id],
  )

  const [open, setOpen] = useState(false)
  const [kind, setKind] = useState<ReviewKindAll>('supervision')
  const [heldOn, setHeldOn] = useState(today())
  const [by, setBy] = useState('')
  const [summary, setSummary] = useState('')
  const [actions, setActions] = useState('')
  const [outcome, setOutcome] = useState('')
  const [concerns, setConcerns] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (result.state === 'loading') return <Loading what="supervisions and appraisals" />
  if (result.state === 'error') return <Failed message={result.message} onRetry={result.reload} />

  const { meetings, cadence, history } = result.data
  const now = today()
  const supervision =
    cadence !== null && history !== null ? nextReviewDue('supervision', history, cadence, now) : null
  const appraisal =
    cadence !== null && history !== null ? nextReviewDue('appraisal', history, cadence, now) : null

  async function save() {
    setBusy(true)
    setError(null)
    try {
      await logReview({
        organisationId: current.id,
        employmentId: person.employment.id,
        personId: person.person.id,
        departmentId: person.departmentId,
        kind,
        heldOn,
        conductedByName: by,
        summary,
        actionsAgreed: actions,
        outcome,
        concernsRaised: concerns,
      })
      setOpen(false)
      setSummary('')
      setActions('')
      setOutcome('')
      setConcerns(false)
      result.reload()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="card">
      <div className="head">
        <h2>Supervisions and appraisals</h2>
        <div className="spacer" />
        <button className="btn sm" type="button" onClick={() => setOpen((v) => !v)}>
          {open ? 'Cancel' : 'Log a meeting'}
        </button>
      </div>

      <div className="cadence">
        <Cadence due={supervision} label="Supervision" />
        <Cadence due={appraisal} label="Appraisal" />
      </div>

      {open && (
        <div className="addform">
          <h3>Log a meeting that has already happened</h3>
          <p style={{ margin: '0 0 14px', fontSize: 13, color: 'var(--ivory-dim)', maxWidth: '66ch' }}>
            Record the date it actually took place, not today. Backdating is correct here: the whole
            cycle is anchored on this date, so entering today for a meeting held last month pushes
            the next one a month late.
          </p>
          <div className="grid">
            <label>
              Kind
              <select value={kind} onChange={(e) => setKind(e.target.value as ReviewKindAll)}>
                <option value="supervision">Supervision</option>
                <option value="appraisal">Appraisal</option>
                <option value="probation_review">Probation review</option>
              </select>
            </label>
            <label>
              Held on
              <input type="date" value={heldOn} max={now} onChange={(e) => setHeldOn(e.target.value)} />
            </label>
            <label>
              Conducted by
              <input
                type="text"
                value={by}
                placeholder="Name, if not you"
                onChange={(e) => setBy(e.target.value)}
              />
            </label>
            <label>
              Outcome
              <input
                type="text"
                value={outcome}
                placeholder="e.g. Meeting expectations"
                onChange={(e) => setOutcome(e.target.value)}
              />
            </label>
            <label className="wide">
              What was discussed
              <textarea value={summary} onChange={(e) => setSummary(e.target.value)} />
            </label>
            <label className="wide">
              Actions agreed
              <textarea value={actions} onChange={(e) => setActions(e.target.value)} />
            </label>
            <label className="check">
              <input
                type="checkbox"
                checked={concerns}
                onChange={(e) => setConcerns(e.target.checked)}
              />
              A concern was raised
            </label>
            <div>
              <button className="btn" type="button" onClick={() => void save()} disabled={busy}>
                {busy ? 'Saving…' : 'Save meeting'}
              </button>
            </div>
          </div>
          {error !== null && (
            <div className="callout" style={{ marginTop: 14, borderLeftColor: 'var(--sev-critical)' }}>
              {error}
            </div>
          )}
        </div>
      )}

      <div className="body flush">
        {meetings.length === 0 ? (
          <div className="empty">
            <p>
              Nothing recorded yet. If meetings have been held and never written down, log them with
              the dates they took place rather than today.
            </p>
          </div>
        ) : (
          <div className="timeline">
            {meetings.map((m) => (
              <Meeting key={m.id} m={m} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
